import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin, message } from 'antd';
import {
  SettingOutlined, EditOutlined, RightOutlined, CalendarOutlined,
  FileDoneOutlined,
  AuditOutlined, CloseCircleOutlined, WalletOutlined, FileTextOutlined,
  InboxOutlined, CarOutlined, CheckCircleOutlined, RollbackOutlined,
  ProfileOutlined, CrownOutlined, EnvironmentOutlined, ReadOutlined,
  CustomerServiceOutlined, ReloadOutlined,
  HistoryOutlined, CalculatorOutlined, ScanOutlined, FormOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import dayjs from 'dayjs';
import PageSkeleton, { ListSkeleton } from '../components/Skeleton';

const TEAL = '#1A6B54';
const GOLD = '#B8892A';
const INK = '#18140C';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';
const RED = '#C0392B';

const BUYER_TIER = {
  v1: { label: 'V1 会员' },
  v2: { label: 'V2 银卡' },
  v3: { label: 'V3 金卡' },
};

// 状态徽章
function Badge({ count }) {
  if (!count || count <= 0) return null;
  return (
    <span style={{
      position: 'absolute', top: -6, right: -10, minWidth: 16, height: 16, padding: '0 4px',
      borderRadius: 8, background: RED, color: '#fff', fontSize: 10, fontWeight: 600,
      lineHeight: '16px', textAlign: 'center', boxSizing: 'border-box',
    }}>{count > 99 ? '99+' : count}</span>
  );
}

// 图标格子（带角标）
function GridItem({ icon, label, count, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: 'none', border: 'none', cursor: 'pointer',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, padding: '10px 0',
    }}>
      <span style={{ position: 'relative', fontSize: 24, color: TEAL, lineHeight: 1 }}>
        {icon}<Badge count={count} />
      </span>
      <span style={{ fontSize: 12, color: MUTED }}>{label}</span>
    </button>
  );
}

// 卡片容器（标题 + 可选"全部"）
function SectionCard({ title, onAll, children, isMobile }) {
  return (
    <div style={{
      margin: `12px ${isMobile ? 0 : 0}px 0`, background: '#fff',
      borderRadius: isMobile ? 0 : 10, border: isMobile ? 'none' : `1px solid ${BD}`,
      overflow: 'hidden',
    }}>
      {title && (
        <div style={{
          padding: '14px 16px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: INK }}>{title}</span>
          {onAll && (
            <button onClick={onAll} style={{
              background: 'none', border: 'none', cursor: 'pointer', color: MUTED, fontSize: 12,
              display: 'flex', alignItems: 'center', gap: 2,
            }}>全部 <RightOutlined style={{ fontSize: 10 }} /></button>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

export default function MyCenter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [distVisible, setDistVisible] = useState(true); // 分销功能展示开关（关闭时隐藏入口）
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadStats = () => {
    api.get('/customers/my-center')
      .then(res => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadStats(); }, []);
  useEffect(() => {
    api.get('/distribution/status').then(({ data }) => setDistVisible(data?.visible !== false)).catch(() => {});
  }, []);

  if (loading) return <ListSkeleton rows={6} />;

  const data = stats || {};
  const phone = user?.phone || '—';
  const maskedPhone = phone.length >= 7 ? phone.slice(0, 3) + '****' + phone.slice(-4) : phone;
  const tier = BUYER_TIER[data.buyer_tier] || BUYER_TIER.v1;
  const available = Number(data.wallet_balance || 0);
  const frozen = Number(data.wallet_frozen || 0);
  const total = available + frozen;

  const handleLogout = () => { logout(); navigate('/'); };

  // 我的竞拍
  const bidItems = [
    { icon: <AuditOutlined />, label: '待开标', count: data.proxy_bidding, to: '/mandates?status=active' },
    { icon: <CloseCircleOutlined />, label: '未中标', count: data.proxy_lost, to: '/mandates?status=lost' },
    { icon: <WalletOutlined />, label: '待支付', count: data.proxy_unpaid, to: '/orders?status=__unpaid__' },
    { icon: <FileTextOutlined />, label: '请求书', count: 0, to: '/bills' },
  ];
  // 我的物流
  const logiItems = [
    { icon: <InboxOutlined />, label: '待发货', count: data.proxy_to_ship, to: '/orders?status=inspecting,pending_receipt,ready_to_ship' },
    { icon: <CarOutlined />, label: '已发货', count: data.proxy_shipping, to: '/orders?status=shipping' },
    { icon: <CheckCircleOutlined />, label: '已完成', count: data.proxy_completed, to: '/orders?status=delivered' },
    { icon: <RollbackOutlined />, label: '售后记录', count: data.proxy_aftersale, to: '/orders?status=refunded' },
  ];
  // 更多服务
  const moreItems = [
    { icon: <ProfileOutlined />, label: '我的订单', to: '/orders' },
    { icon: <HistoryOutlined />, label: '历史成交价', to: '/valuation' },
    { icon: <CalculatorOutlined />, label: '费用计算器', to: '/fee-calculator' },
    { icon: <FileDoneOutlined />, label: '支付凭证', to: '/payment-vouchers' },
    { icon: <ScanOutlined />, label: '智能识瑕疵', to: '/valuation' },
    { icon: <CarOutlined />, label: '物流单', to: '/logistics' },
    { icon: <CrownOutlined />, label: t('membership.buyer'), to: '/membership/buyer' },
    { icon: <EnvironmentOutlined />, label: t('orders.shippingAddress'), to: '/profile' },
    { icon: <WalletOutlined />, label: t('myCenter.myDeposit'), to: '/deposit' },
    { icon: <ReadOutlined />, label: '代拍业务说明', to: '/auction-intro' },
    { icon: <FormOutlined />, label: '意见反馈', to: '/feedback' },
    { icon: <SettingOutlined />, label: t('myCenter.settings'), to: '/settings' },
  ];

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 40 }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: isMobile ? 0 : '0 16px' }}>

        {/* ── ① 头部 ── */}
        <div style={{
          background: 'linear-gradient(135deg, #1A2E28 0%, #1F3A32 60%, #2A4A3E 100%)',
          padding: isMobile ? '22px 18px 18px' : '26px 22px 20px',
          borderRadius: isMobile ? 0 : '0 0 12px 12px', position: 'relative',
        }}>
          <button onClick={() => navigate('/settings')} style={{
            position: 'absolute', top: 16, right: 16, background: 'none', border: 'none',
            cursor: 'pointer', color: 'rgba(255,255,255,.6)', fontSize: 18,
          }}><SettingOutlined /></button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 54, height: 54, borderRadius: '50%', background: 'rgba(255,255,255,.15)',
              border: '2px solid rgba(255,255,255,.2)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 22, color: '#fff', flexShrink: 0,
            }}>{(user?.name || maskedPhone || '?').charAt(0)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#fff', marginBottom: 2 }}>{maskedPhone}</div>
              {data.customer_no && <div style={{ fontSize: 11, color: 'rgba(255,255,255,.72)', fontFamily: 'var(--mono,monospace)', marginBottom: 6 }}>会员编号 {String(data.customer_no).slice(-4)}</div>}
              <button onClick={() => navigate('/membership/buyer')} style={{
                padding: '2px 12px', borderRadius: 12, border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg, #E0B65C, #C8922A)', color: '#fff',
                fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4,
              }}><CrownOutlined style={{ fontSize: 11 }} /> {tier.label} <RightOutlined style={{ fontSize: 9 }} /></button>
            </div>
            <button onClick={() => navigate('/profile')} style={{
              padding: '5px 12px', borderRadius: 14, border: '1px solid rgba(255,255,255,.3)',
              background: 'transparent', color: 'rgba(255,255,255,.85)', fontSize: 11, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
            }}><EditOutlined style={{ fontSize: 11 }} /> {t('profile.title')}</button>
          </div>

          {/* 3 统计 */}
          <div style={{ display: 'flex', marginTop: 18 }}>
            {[
              { val: data.watchlist_count || 0, label: '收藏', to: '/watchlist' },
              { val: data.proxy_bidding || 0, label: '委托中', to: '/mandates' },
              { val: data.unread_count || 0, label: '消息', to: '/notifications' },
            ].map(s => (
              <button key={s.label} onClick={() => navigate(s.to)} style={{
                flex: 1, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'center',
              }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', fontFamily: 'var(--mono,monospace)' }}>{s.val}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', marginTop: 2 }}>{s.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* ── ①.5 合作伙伴中心入口:功能展示开关开启 且 有效伙伴身份(promoter/agent 且未停权) ── */}
        {distVisible && data.partner_active && (
          <button onClick={() => navigate('/partner')} style={{
            display: 'flex', alignItems: 'center', gap: 12, width: '100%',
            margin: '12px 0 0', padding: '13px 16px',
            background: '#fff', border: `1px solid ${BD}`, borderRadius: isMobile ? 0 : 10, cursor: 'pointer',
          }}>
            <span style={{
              width: 34, height: 34, borderRadius: 9, flexShrink: 0,
              background: 'linear-gradient(135deg, #E0B65C, #C8922A)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
            }}>🤝</span>
            <span style={{ flex: 1, textAlign: 'left', fontSize: 14, fontWeight: 600, color: INK }}>
              合作伙伴中心
            </span>
            <span style={{ fontSize: 11, color: MUTED, marginRight: 2 }}>
              {data.distribution_role === 'agent' ? '代理' : '推荐人'}
            </span>
            <RightOutlined style={{ fontSize: 12, color: '#ccc' }} />
          </button>
        )}

        {/* ── ①.6 专属顾问（被推荐客户可见；受功能展示开关控制）── */}
        {distVisible && data.advisor_name && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            margin: '12px 0 0', padding: '10px 16px',
            background: 'rgba(26,107,84,.05)', border: `1px solid ${BD}`,
            borderRadius: isMobile ? 0 : 10, fontSize: 12, color: MUTED,
          }}>
            <span>👤</span>
            <span>专属顾问：<b style={{ color: INK }}>{data.advisor_name}</b></span>
          </div>
        )}

        {/* ── ② 拍卖日历横幅（白卡 + 深绿金色图标 chip，融入系统调性）── */}
        <button onClick={() => navigate('/session-calendar')} style={{
          display: 'flex', alignItems: 'center', gap: 12, width: '100%',
          margin: '12px 0 0', padding: '13px 16px',
          background: '#fff', border: `1px solid ${BD}`, borderRadius: isMobile ? 0 : 10, cursor: 'pointer',
        }}>
          <span style={{
            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
            background: 'linear-gradient(135deg, #1F3A32, #2A4A3E)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CalendarOutlined style={{ fontSize: 17, color: '#E0B65C' }} />
          </span>
          <span style={{ flex: 1, textAlign: 'left', fontSize: 14, fontWeight: 600, color: INK }}>
            {dayjs().month() + 1}月拍卖日历
          </span>
          <span style={{ fontSize: 11, color: MUTED, marginRight: 2 }}>查看排期</span>
          <RightOutlined style={{ fontSize: 12, color: '#ccc' }} />
        </button>

        {/* ── ③ 我的竞拍 ── */}
        <SectionCard title="我的竞拍" isMobile={isMobile}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', padding: '4px 0 12px' }}>
            {bidItems.map(it => <GridItem key={it.label} {...it} onClick={() => navigate(it.to)} />)}
          </div>
        </SectionCard>

        {/* ── ④ 我的物流 ── */}
        <SectionCard title="我的物流" onAll={() => navigate('/orders')} isMobile={isMobile}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', padding: '4px 0 12px' }}>
            {logiItems.map(it => <GridItem key={it.label} {...it} onClick={() => navigate(it.to)} />)}
          </div>
        </SectionCard>

        {/* ── ⑤ 账户明细 ── */}
        <SectionCard
          title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>账户明细 <ReloadOutlined style={{ fontSize: 12, color: MUTED }} onClick={(e) => { e.stopPropagation(); loadStats(); }} /></span>}
          onAll={() => navigate('/deposit')} isMobile={isMobile}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', padding: '6px 0 16px' }}>
            {[
              { val: total, label: '全部金额' },
              { val: available, label: '可用金额' },
              { val: frozen, label: '待释放' },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 19, fontWeight: 700, color: TEAL, fontFamily: 'var(--mono,monospace)' }}>
                  {s.val.toFixed(2)}
                </div>
                <div style={{ fontSize: 11, color: MUTED, marginTop: 3 }}>{s.label} <span style={{ fontSize: 9, opacity: .7 }}>(RMB)</span></div>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ── ⑥ 更多服务 ── */}
        <SectionCard title="更多服务" isMobile={isMobile}>
          <div>
            {moreItems.map((it, i) => (
              <button key={it.label} onClick={() => navigate(it.to)} style={{
                width: '100%', padding: '14px 16px', background: 'none', border: 'none',
                borderTop: i === 0 ? `1px solid ${BD}30` : 'none',
                borderBottom: i < moreItems.length - 1 ? `1px solid ${BD}30` : 'none',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{ fontSize: 17, color: MUTED }}>{it.icon}</span>
                <span style={{ fontSize: 14, color: INK }}>{it.label}</span>
                <RightOutlined style={{ marginLeft: 'auto', fontSize: 11, color: '#ccc' }} />
              </button>
            ))}
          </div>
        </SectionCard>

        {/* ── ⑦ 客服 + 退出 ── */}
        <div style={{ margin: `20px ${isMobile ? 16 : 0}px`, textAlign: 'center' }}>
          <button onClick={() => navigate('/support')} style={{
            padding: '9px 28px', borderRadius: 22, border: `1px solid ${TEAL}`, background: 'transparent',
            color: TEAL, fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
          }}><CustomerServiceOutlined /> {t('support.title')}</button>
          <div style={{ marginTop: 14 }}>
            <button onClick={handleLogout} style={{
              background: 'none', border: 'none', color: RED, fontSize: 13, cursor: 'pointer',
            }}>{t('auth.logout')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
